import { useState } from "react";
import { motion } from "framer-motion";
import {
  Sparkles, Calculator, FileText, Palette, Image as ImageIcon, Wand2,
  X, Loader2, Download, Copy, Check,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.4 } }),
};

const TOOLS = [
  {
    id: "budget",
    name: "Budget Calculator",
    desc: "Detailed line-item budget for any event",
    icon: Calculator,
    accent: "text-emerald-300",
    glow: "from-emerald-500/20",
  },
  {
    id: "contract",
    name: "Contract Drafter",
    desc: "Professional event services contract",
    icon: FileText,
    accent: "text-amber-300",
    glow: "from-amber-500/20",
  },
  {
    id: "style",
    name: "Style Recommender",
    desc: "Palette, decor & theme guidance",
    icon: Palette,
    accent: "text-rose-300",
    glow: "from-rose-500/20",
  },
  {
    id: "poster",
    name: "Event Poster",
    desc: "Generate a luxury event poster in seconds",
    icon: ImageIcon,
    accent: "text-violet-300",
    glow: "from-violet-500/20",
  },
  {
    id: "photo_booth",
    name: "AI Photo Booth",
    desc: "Magazine-cover portrait from your selfie",
    icon: Wand2,
    accent: "text-cyan-300",
    glow: "from-cyan-500/20",
  },
];

export default function AiToolkit() {
  const [active, setActive] = useState(null);

  return (
    <motion.div data-testid="ai-toolkit" initial="hidden" animate="visible">
      <motion.div variants={fadeUp} custom={0} className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <Sparkles size={20} className="text-gold" />
          <h2 className="font-heading text-2xl text-white">AI Concierge Toolkit</h2>
        </div>
        <p className="text-white/40 font-body text-sm">
          Five AI tools tuned to luxury events. Powered by GPT-5.2 and ready in seconds.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {TOOLS.map((tool, i) => (
          <motion.button
            key={tool.id}
            variants={fadeUp}
            custom={i + 1}
            data-testid={`ai-tool-${tool.id}`}
            onClick={() => setActive(tool.id)}
            className={`group relative text-left glass-card p-6 border border-white/10 hover:border-gold/40 transition-all overflow-hidden`}
          >
            <div className={`absolute inset-0 bg-gradient-to-br ${tool.glow} to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
            <div className="relative">
              <tool.icon size={26} className={`${tool.accent} mb-4`} />
              <h3 className="text-white font-body font-medium text-base mb-1.5">{tool.name}</h3>
              <p className="text-white/40 font-body font-light text-xs leading-relaxed mb-4">{tool.desc}</p>
              <span className="inline-flex items-center gap-1.5 text-gold text-xs font-body uppercase tracking-wider">
                Launch <Sparkles size={11} />
              </span>
            </div>
          </motion.button>
        ))}
      </div>

      {active && <ToolModal toolId={active} onClose={() => setActive(null)} />}
    </motion.div>
  );
}

function ToolModal({ toolId, onClose }) {
  const tool = TOOLS.find((t) => t.id === toolId);
  return (
    <div
      data-testid={`ai-tool-modal-${toolId}`}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl bg-luxury-black border border-gold/30 my-12"
      >
        <div className="flex items-start justify-between p-6 border-b border-white/5">
          <div className="flex items-center gap-3">
            <tool.icon size={22} className={tool.accent} />
            <div>
              <h3 className="font-heading text-xl text-white">{tool.name}</h3>
              <p className="text-white/40 font-body text-xs">{tool.desc}</p>
            </div>
          </div>
          <button data-testid="ai-tool-close" onClick={onClose} className="text-white/40 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>
        <div className="p-6">
          {toolId === "budget" && <BudgetTool />}
          {toolId === "contract" && <ContractTool />}
          {toolId === "style" && <StyleTool />}
          {toolId === "poster" && <PosterTool />}
          {toolId === "photo_booth" && <PhotoBoothTool />}
        </div>
      </motion.div>
    </div>
  );
}

// ============== SHARED ATOMS ==============

function FieldLabel({ children }) {
  return <label className="block text-xs font-body uppercase tracking-wider text-white/40 mb-1.5">{children}</label>;
}

function TextField({ value, onChange, placeholder, ...rest }) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-white/5 border border-white/10 focus:border-gold/40 px-4 py-2.5 text-sm font-body text-white placeholder-white/30 outline-none transition-colors"
      {...rest}
    />
  );
}

function Textarea({ value, onChange, placeholder, rows = 4 }) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full bg-white/5 border border-white/10 focus:border-gold/40 px-4 py-2.5 text-sm font-body text-white placeholder-white/30 outline-none transition-colors resize-none"
    />
  );
}

function GoldCta({ onClick, busy, children, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={busy}
      className="inline-flex items-center gap-2 bg-gold/15 hover:bg-gold/25 disabled:opacity-40 disabled:cursor-not-allowed text-gold border border-gold/40 px-5 py-2.5 text-sm font-body uppercase tracking-wider transition-colors"
    >
      {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
      {children}
    </button>
  );
}

function ResultBlock({ text, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Copy failed"); }
  };
  return (
    <div data-testid={testId} className="bg-black/40 border border-white/10 p-4 mt-5 relative">
      <button
        onClick={copy}
        className="absolute top-3 right-3 text-white/40 hover:text-gold transition-colors"
        data-testid={`${testId}-copy`}
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>
      <pre className="whitespace-pre-wrap text-white/80 font-body text-xs leading-relaxed max-h-[420px] overflow-y-auto pr-8">
        {text}
      </pre>
    </div>
  );
}

// ============== BUDGET ==============

function BudgetTool() {
  const [eventType, setEventType] = useState("");
  const [guestCount, setGuestCount] = useState(50);
  const [location, setLocation] = useState("Jamaica");
  const [budgetPref, setBudgetPref] = useState("premium");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");

  const run = async () => {
    if (!eventType.trim()) return toast.error("Enter an event type");
    setBusy(true); setResult("");
    try {
      const r = await axios.post(`${API}/ai/budget-calculator`,
        { event_type: eventType, guest_count: guestCount, location, budget_preference: budgetPref },
        { withCredentials: true });
      setResult(r.data.budget || "No response from AI");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI service unavailable");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <FieldLabel>Event type</FieldLabel>
          <TextField value={eventType} onChange={setEventType} placeholder="Wedding, Birthday, Corporate Gala…" data-testid="budget-event-type" />
        </div>
        <div>
          <FieldLabel>Guest count</FieldLabel>
          <input
            type="number" min={1} max={2000}
            value={guestCount}
            onChange={(e) => setGuestCount(parseInt(e.target.value) || 50)}
            data-testid="budget-guest-count"
            className="w-full bg-white/5 border border-white/10 focus:border-gold/40 px-4 py-2.5 text-sm font-body text-white outline-none transition-colors"
          />
        </div>
        <div>
          <FieldLabel>Location</FieldLabel>
          <TextField value={location} onChange={setLocation} placeholder="Kingston, Montego Bay, Miami…" data-testid="budget-location" />
        </div>
        <div>
          <FieldLabel>Budget level</FieldLabel>
          <select
            value={budgetPref}
            onChange={(e) => setBudgetPref(e.target.value)}
            data-testid="budget-pref"
            className="w-full bg-white/5 border border-white/10 focus:border-gold/40 px-4 py-2.5 text-sm font-body text-white outline-none transition-colors"
          >
            <option value="essential">Essential</option>
            <option value="premium">Premium</option>
            <option value="luxury">Luxury</option>
            <option value="no-limit">No limit</option>
          </select>
        </div>
      </div>
      <GoldCta onClick={run} busy={busy} testId="budget-run">Generate budget</GoldCta>
      {result && <ResultBlock text={result} testId="budget-result" />}
    </div>
  );
}

// ============== CONTRACT ==============

function ContractTool() {
  const [clientName, setClientName] = useState("");
  const [eventType, setEventType] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [totalAmount, setTotalAmount] = useState(5000);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");

  const run = async () => {
    if (!clientName.trim() || !eventType.trim()) return toast.error("Client name + event type required");
    setBusy(true); setResult("");
    try {
      const r = await axios.post(`${API}/ai/generate-contract`,
        { client_name: clientName, event_type: eventType, event_date: eventDate, total_amount: totalAmount },
        { withCredentials: true });
      setResult(r.data.contract || "No response from AI");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI service unavailable");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <FieldLabel>Client name</FieldLabel>
          <TextField value={clientName} onChange={setClientName} placeholder="Full legal name" data-testid="contract-client-name" />
        </div>
        <div>
          <FieldLabel>Event type</FieldLabel>
          <TextField value={eventType} onChange={setEventType} placeholder="Wedding, Anniversary…" data-testid="contract-event-type" />
        </div>
        <div>
          <FieldLabel>Event date</FieldLabel>
          <input
            type="date"
            value={eventDate}
            onChange={(e) => setEventDate(e.target.value)}
            data-testid="contract-date"
            className="w-full bg-white/5 border border-white/10 focus:border-gold/40 px-4 py-2.5 text-sm font-body text-white outline-none transition-colors"
          />
        </div>
        <div>
          <FieldLabel>Total (USD)</FieldLabel>
          <input
            type="number" min={0} step={100}
            value={totalAmount}
            onChange={(e) => setTotalAmount(parseFloat(e.target.value) || 0)}
            data-testid="contract-total"
            className="w-full bg-white/5 border border-white/10 focus:border-gold/40 px-4 py-2.5 text-sm font-body text-white outline-none transition-colors"
          />
        </div>
      </div>
      <GoldCta onClick={run} busy={busy} testId="contract-run">Draft contract</GoldCta>
      {result && <ResultBlock text={result} testId="contract-result" />}
    </div>
  );
}

// ============== STYLE ==============

function StyleTool() {
  const [venue, setVenue] = useState("");
  const [eventType, setEventType] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");

  const run = async () => {
    if (!venue.trim()) return toast.error("Describe your venue");
    setBusy(true); setResult("");
    try {
      const r = await axios.post(`${API}/ai/style-recommender`,
        { venue_description: venue, event_type: eventType },
        { withCredentials: true });
      setResult(r.data.recommendations || "No response from AI");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI service unavailable");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div>
        <FieldLabel>Venue description</FieldLabel>
        <Textarea value={venue} onChange={setVenue} placeholder="A 60-foot-high glasshouse overlooking the Caribbean, white marble floors, late-afternoon sun, capacity 120…" rows={4} />
      </div>
      <div>
        <FieldLabel>Event type (optional)</FieldLabel>
        <TextField value={eventType} onChange={setEventType} placeholder="Wedding reception, gala dinner…" />
      </div>
      <GoldCta onClick={run} busy={busy} testId="style-run">Get style ideas</GoldCta>
      {result && <ResultBlock text={result} testId="style-result" />}
    </div>
  );
}

// ============== POSTER ==============

function PosterTool() {
  const [description, setDescription] = useState("");
  const [style, setStyle] = useState("luxury");
  const [busy, setBusy] = useState(false);
  const [imageBase64, setImageBase64] = useState("");

  const run = async () => {
    if (!description.trim()) return toast.error("Describe the poster");
    setBusy(true); setImageBase64("");
    try {
      const r = await axios.post(`${API}/ai/generate-poster`,
        { description, style },
        { withCredentials: true });
      setImageBase64(r.data.image_base64 || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI service unavailable");
    } finally { setBusy(false); }
  };

  const download = () => {
    const a = document.createElement("a");
    a.href = `data:image/png;base64,${imageBase64}`;
    a.download = "ajja-poster.png";
    a.click();
  };

  return (
    <div className="space-y-4">
      <div>
        <FieldLabel>Describe the poster</FieldLabel>
        <Textarea value={description} onChange={setDescription} placeholder="Midnight rooftop gala with city lights, gold accents, women in floor-length gowns, June 15, Devon House Kingston…" rows={4} />
      </div>
      <div>
        <FieldLabel>Style</FieldLabel>
        <select
          value={style}
          onChange={(e) => setStyle(e.target.value)}
          className="w-full bg-white/5 border border-white/10 focus:border-gold/40 px-4 py-2.5 text-sm font-body text-white outline-none transition-colors"
        >
          <option value="luxury">Luxury black & gold</option>
          <option value="minimalist">Minimalist editorial</option>
          <option value="art-deco">Art Deco</option>
          <option value="tropical">Tropical glam</option>
        </select>
      </div>
      <GoldCta onClick={run} busy={busy} testId="poster-run">Generate poster</GoldCta>
      {imageBase64 && (
        <div data-testid="poster-result" className="mt-5">
          <img src={`data:image/png;base64,${imageBase64}`} alt="AI poster" className="w-full max-w-md mx-auto border border-gold/20" />
          <div className="text-center mt-3">
            <button data-testid="poster-download" onClick={download} className="inline-flex items-center gap-2 text-gold text-xs font-body uppercase tracking-wider hover:text-gold-light transition-colors">
              <Download size={14} /> Download PNG
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============== PHOTO BOOTH ==============

function PhotoBoothTool() {
  const [file, setFile] = useState(null);
  const [style, setStyle] = useState("luxury portrait");
  const [busy, setBusy] = useState(false);
  const [imageBase64, setImageBase64] = useState("");

  const run = async () => {
    if (!file) return toast.error("Pick a photo first");
    setBusy(true); setImageBase64("");
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await axios.post(`${API}/ai/photo-booth`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data", "X-Style": style },
      });
      setImageBase64(r.data.image_base64 || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI service unavailable");
    } finally { setBusy(false); }
  };

  const download = () => {
    const a = document.createElement("a");
    a.href = `data:image/png;base64,${imageBase64}`;
    a.download = "ajja-portrait.png";
    a.click();
  };

  return (
    <div className="space-y-4">
      <div>
        <FieldLabel>Your photo (max 10 MB)</FieldLabel>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          data-testid="photobooth-file"
          className="block w-full text-xs text-white/60 file:mr-4 file:py-2 file:px-4 file:border file:border-gold/40 file:bg-gold/10 file:text-gold file:text-xs file:font-body file:uppercase file:tracking-wider file:cursor-pointer hover:file:bg-gold/20"
        />
        {file && <p className="text-white/40 text-xs font-body mt-2">{file.name} · {(file.size / 1024).toFixed(0)} KB</p>}
      </div>
      <div>
        <FieldLabel>Style</FieldLabel>
        <select
          value={style}
          onChange={(e) => setStyle(e.target.value)}
          className="w-full bg-white/5 border border-white/10 focus:border-gold/40 px-4 py-2.5 text-sm font-body text-white outline-none transition-colors"
        >
          <option value="luxury portrait">Luxury portrait</option>
          <option value="magazine cover">Magazine cover</option>
          <option value="black & gold editorial">Black & gold editorial</option>
          <option value="cinematic noir">Cinematic noir</option>
        </select>
      </div>
      <GoldCta onClick={run} busy={busy} testId="photobooth-run">Transform photo</GoldCta>
      {imageBase64 && (
        <div data-testid="photobooth-result" className="mt-5">
          <img src={`data:image/png;base64,${imageBase64}`} alt="AI portrait" className="w-full max-w-md mx-auto border border-gold/20" />
          <div className="text-center mt-3">
            <button data-testid="photobooth-download" onClick={download} className="inline-flex items-center gap-2 text-gold text-xs font-body uppercase tracking-wider hover:text-gold-light transition-colors">
              <Download size={14} /> Download portrait
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
